// Detail panel — shows selected entity details and cross-links

import { Link, Plus, X } from 'lucide-react'
import type { SpaceNode, CrossLink, DualSpaceData, ConfidenceLevel } from '@ispot/shared'

interface DetailPanelProps {
  selectedNode: SpaceNode | null
  data: DualSpaceData | null
  onUnlink?: (linkId: string) => void
  /** When embedded in tabbed panel, remove outer width/border */
  embedded?: boolean
}

const CONFIDENCE_LABELS: Record<ConfidenceLevel, string> = {
  hypothesis: 'Hypothesis',
  signal: 'Signal',
  pattern: 'Pattern',
  validated: 'Validated',
  contested: 'Contested',
  decided: 'Decided',
}

const CONFIDENCE_COLORS: Record<ConfidenceLevel, string> = {
  hypothesis: 'bg-warm-400',
  signal: 'bg-business',
  pattern: 'bg-solution',
  validated: 'bg-market',
  contested: 'bg-danger',
  decided: 'bg-warm-500',
}

const BADGE_CLASSES: Record<string, string> = {
  persona: 'bg-warm-200 text-warm-700',
  jtbd: 'bg-business/10 text-business',
  pain: 'bg-danger/10 text-danger',
  gain: 'bg-market/10 text-market',
  need: 'bg-solution/12 text-solution-dark',
  capability: 'bg-core/10 text-core',
  feature: 'bg-market/10 text-market',
  benefit: 'bg-business/10 text-business',
}

export function DetailPanel({ selectedNode, data, onUnlink, embedded }: DetailPanelProps) {
  const wrapperClass = embedded
    ? 'flex-1 flex flex-col overflow-hidden'
    : 'w-[300px] border-l border-border bg-warm-100 flex flex-col flex-shrink-0 overflow-hidden'

  if (!selectedNode) {
    return (
      <div className={embedded ? 'flex-1 flex items-center justify-center' : 'w-[300px] border-l border-border bg-warm-100 flex items-center justify-center flex-shrink-0'}>
        <div className="text-center text-text-muted text-[13px] px-10">
          <X size={32} className="mx-auto mb-3 opacity-30" />
          Select a node to see its<br />details and cross-links
        </div>
      </div>
    )
  }

  // Find linked nodes
  const linkedNodes = getLinkedNodes(selectedNode, data)
  const isProblemType = ['persona', 'alternative', 'jtbd', 'pain', 'gain', 'need'].includes(selectedNode.type)
  const otherSpace = isProblemType ? 'solution' : 'problem'

  return (
    <div className={wrapperClass}>
      {/* Header */}
      <div className="px-5 pt-4 pb-3 border-b border-border-subtle">
        <div className="flex items-center gap-2 mb-2">
          <span className={`font-display text-[10px] font-semibold uppercase tracking-wide px-1.5 py-px rounded-sm ${BADGE_CLASSES[selectedNode.type] || 'bg-warm-200 text-warm-600'}`}>
            {selectedNode.type}
          </span>
          <div className="flex items-center gap-1.5 text-[11px] text-text-muted">
            <div className={`w-2 h-2 rounded-full ${CONFIDENCE_COLORS[selectedNode.confidenceLevel]}`} />
            {CONFIDENCE_LABELS[selectedNode.confidenceLevel]} · {selectedNode.confidenceScore}%
          </div>
        </div>
        <h3 className="font-display text-base font-semibold text-text leading-tight">
          {selectedNode.title}
        </h3>
      </div>

      {/* Cross-links */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        <div className="mb-5">
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-text-muted mb-2">
            Links to {otherSpace} space ({linkedNodes.length})
          </h4>
          {linkedNodes.length > 0 ? (
            <div className="space-y-1">
              {linkedNodes.map(({ node, linkId }) => (
                <div key={node.id} className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-surface border border-border-subtle hover:border-border transition-all group">
                  <span className={`font-display text-[9px] font-semibold uppercase px-1 rounded-sm ${BADGE_CLASSES[node.type] || 'bg-warm-200 text-warm-600'}`}>
                    {node.type}
                  </span>
                  <span className="text-[12px] text-text-secondary flex-1 min-w-0 truncate">
                    {node.title}
                  </span>
                  {onUnlink && linkId && (
                    <button
                      onClick={() => onUnlink(linkId)}
                      className="w-[18px] h-[18px] flex items-center justify-center text-warm-400 hover:text-danger hover:bg-danger/[0.08] rounded-sm opacity-0 group-hover:opacity-100 transition-all"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[12px] text-text-muted italic">
              No cross-links yet. Drag to link or use the button below.
            </p>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="px-5 py-3 border-t border-border-subtle space-y-1.5">
        <button className="flex items-center gap-1.5 w-full text-[12px] text-warm-100 bg-warm-800 hover:bg-warm-900 border border-warm-800 rounded-md px-3 py-1.5 transition-colors">
          <Link size={14} /> Link to other space
        </button>
        <button className="flex items-center gap-1.5 w-full text-[12px] text-text-secondary bg-surface hover:bg-warm-50 border border-border rounded-md px-3 py-1.5 transition-colors">
          <Plus size={14} /> Add child
        </button>
      </div>
    </div>
  )
}

function getLinkedNodes(
  selected: SpaceNode,
  data: DualSpaceData | null,
): { node: SpaceNode; linkId: string | null }[] {
  if (!data) return []

  const results: { node: SpaceNode; linkId: string | null }[] = []
  const isProblem = ['persona', 'alternative', 'jtbd', 'pain', 'gain', 'need'].includes(selected.type)
  const searchSpace = isProblem ? data.solutionSpace : data.problemSpace

  for (const linkedId of selected.crossLinkIds) {
    const node = findInTree(searchSpace, linkedId)
    if (node) {
      const link = data.crossLinks.find(
        l => (l.sourceId === selected.id && l.targetId === linkedId)
          || (l.targetId === selected.id && l.sourceId === linkedId),
      )
      results.push({ node, linkId: link?.id || null })
    }
  }

  return results
}

function findInTree(nodes: SpaceNode[], id: string): SpaceNode | null {
  for (const node of nodes) {
    if (node.id === id) return node
    const found = findInTree(node.children, id)
    if (found) return found
  }
  return null
}
